import { type useUser } from "@clerk/nextjs";
import { type User } from "@prisma/client";

import { Text, TextProps, tokens } from "~/ui";
import { logger } from "~/utils/logger";

type UserResource = NonNullable<ReturnType<typeof useUser>["user"]>;
type BaseUser = Pick<User, "firstName" | "lastName" | "emailAddress"> | UserResource;

const userIsResource = (user: BaseUser): user is UserResource => Array.isArray((user as UserResource).emailAddresses);

const parseUserEmailAddress = (user: BaseUser): string | null => {
  if (!userIsResource(user)) {
    return user.emailAddress;
  }
  const primaryAddressId = user.primaryEmailAddressId;
  if (primaryAddressId !== null) {
    const primaryEmail = user.emailAddresses.find(e => primaryAddressId === e.id);
    if (primaryEmail) {
      return primaryEmail.emailAddress;
    }
  }
  logger.warn({ clerkUserId: user.id }, "User does not have a primary email address associated with Clerk.");
  return null;
};

/**
 * Returns a string display name for the {@link User} based first on the 'firstName' and/or 'lastName' and then the 'emailAddress' as a
 * fallback.
 *
 * @template {U} user
 *   The generic form of the user that is being provided, which must contain a 'firstName', 'lastName' and 'emailAddress' field.
 *
 * @param {U} user The user for which the display name should be parsed.
 * @returns {string}
 */
export const parseUserDisplayName = <U extends BaseUser = BaseUser, FB extends string | null = null>(
  user: U,
  options?: { fallback?: FB },
): string | FB => {
  const firstName = user.firstName ? user.firstName.trim() : null;
  const lastName = user.lastName ? user.lastName.trim() : null;
  if (firstName && lastName) {
    return `${firstName} ${lastName}`;
  } else if (firstName) {
    return firstName;
  } else if (lastName) {
    return lastName;
  }
  const email = parseUserEmailAddress(user);
  if (email) {
    return email;
  }
  return options?.fallback === undefined ? (null as FB) : options?.fallback;
};

export type UserFullNameTextProps<U extends BaseUser> = Omit<TextProps, "children"> & {
  readonly user: U;
};

export const UserFullNameText = <U extends BaseUser>({ user, ...props }: UserFullNameTextProps<U>): JSX.Element => {
  const name = parseUserDisplayName(user);
  if (!name) {
    return <></>;
  }
  return (
    <Text
      size="sm"
      color="gray.7"
      maw="100%"
      {...props}
      style={{
        fontWeight: tokens.typography.fontWeight.medium,
        textOverflow: "ellipsis",
        overflow: "hidden",
        whiteSpace: "nowrap",
        ...(props.style ?? {}),
      }}
    >
      {name}
    </Text>
  );
};
